import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { fmt } from "@/lib/api";
import { PageHeader, StatCard, PackageBadge, StatusPill, Loader } from "@/components/ui-bits";
import { Users, UserCheck, Network, Copy, Share2, Layers } from "lucide-react";

export default function Referrals() {
  const [d, setD] = useState(null);

  useEffect(() => { API.get("/referrals").then((r) => setD(r.data)); }, []);
  if (!d) return <Loader label="Loading referrals" />;

  const copy = (text, msg) => { navigator.clipboard.writeText(text); toast.success(msg); };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Join my network", url: d.referral_link }); } catch {} }
    else copy(d.referral_link, "Link copied");
  };

  return (
    <div>
      <PageHeader title="Referral Dashboard" subtitle="Share your link, grow your direct team and track downline earnings." testid="referrals-header" />

      <div className="glass rounded-2xl p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Your referral link</div>
            <div className="font-num text-sm text-emerald-300 break-all bg-white/5 rounded-xl px-4 py-3 border border-white/10">{d.referral_link}</div>
          </div>
          <div className="flex gap-2">
            <button data-testid="copy-link" onClick={() => copy(d.referral_link, "Link copied")} className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold px-4 py-3 rounded-xl"><Copy className="h-4 w-4" /> Copy</button>
            <button data-testid="share-link" onClick={share} className="inline-flex items-center gap-2 border border-white/15 hover:border-emerald-500/40 text-white font-semibold px-4 py-3 rounded-xl"><Share2 className="h-4 w-4" /> Share</button>
            <button data-testid="copy-code" onClick={() => copy(d.referral_code, "Code copied")} className="inline-flex items-center gap-2 border border-white/15 hover:border-emerald-500/40 text-white font-num font-semibold px-4 py-3 rounded-xl">{d.referral_code}</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard testid="ref-direct" label="Direct Referrals" value={d.total_direct} icon={Users} accent="emerald" />
        <StatCard testid="ref-active" label="Active Directs" value={d.active_direct} icon={UserCheck} accent="cyan" />
        <StatCard testid="ref-team" label="Total Team (6 lvl)" value={d.total_team} icon={Network} accent="violet" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2 mb-4"><Layers className="h-5 w-5 text-cyan-400" /> Team by Level</h3>
          <div className="space-y-2">
            {[1, 2, 3, 4, 5, 6].map((l) => (
              <div key={l} className="flex items-center justify-between text-sm py-1.5 border-b border-white/5 last:border-0">
                <span className="text-slate-400">Level {l}</span>
                <span className="font-num text-white">{d.team_by_level[`level_${l}`] || 0}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="direct-referrals-list">
          <h3 className="font-display text-lg font-semibold text-white mb-4">Direct Referrals</h3>
          {d.direct_referrals.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">No direct referrals yet. Share your link to grow.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-white/10">
                  <th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Code</th><th className="py-2 pr-4">Package</th><th className="py-2 pr-4">Status</th><th className="py-2 text-right">Earnings</th>
                </tr></thead>
                <tbody>
                  {d.direct_referrals.map((r) => (
                    <tr key={r.id} className="border-b border-white/5 last:border-0">
                      <td className="py-3 pr-4 text-white">{r.name}</td>
                      <td className="py-3 pr-4 font-num text-slate-400">{r.referral_code}</td>
                      <td className="py-3 pr-4"><PackageBadge id={r.package_id} /></td>
                      <td className="py-3 pr-4"><StatusPill status={r.status} /></td>
                      <td className="py-3 text-right font-num text-white">{fmt(r.earnings)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
